import React, { useState, useEffect } from "react";
import {
  X,
  UserX,
  AlertTriangle,
  Folder,
  Shield,
  File,
  FileText,
} from "lucide-react";

const BanFolderModal = ({
  open,
  onClose,
  folder,
  invitedMembers,
  currentUser,
  onBan,
  loading = false,
  sharedFolders = [],
  sharedFiles = [],
}) => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState("");
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedMember(null);
      setConfirmText("");
      setError("");
      setShowWarning(false);
    }
  }, [open]);

  if (!open || !folder) return null;

  // is_me가 true인 멤버는 제외 (자기 자신은 추방할 수 없음)
  const banableMembers = invitedMembers.filter((member) => !member.is_me);

  const handleSelectMember = (member) => {
    setSelectedMember(member);
    setShowWarning(true);
    setError("");
  };

  const handleBan = async () => {
    if (!selectedMember) return;

    if (confirmText !== selectedMember.user_name) {
      setError("사용자 이름이 일치하지 않습니다.");
      return;
    }

    try {
      const result = await onBan(selectedMember.id, folder.id);
      if (result) {
        onClose();
      } else {
        setError("멤버 추방에 실패했습니다.");
      }
    } catch (err) {
      setError("추방 처리 중 오류가 발생했습니다.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <UserX className="w-5 h-5 text-red-600" />
            폴더 멤버 추방
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 본문 */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 text-red-800 text-sm">
              <AlertTriangle className="w-4 h-4" />
              {error}
            </div>
          )}

          {/* 폴더 정보 */}
          <div className="mb-6 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Folder className="w-5 h-5 text-blue-500" />
              <div>
                <p className="font-medium text-gray-900">
                  {folder.folder_name}
                </p>
                {folder.path && (
                  <p className="text-xs text-gray-500">{folder.path}</p>
                )}
              </div>
            </div>
          </div>

          {!showWarning ? (
            <>
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                추방할 멤버 선택
              </h3>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {banableMembers.length === 0 ? (
                  <p className="text-center py-4 text-gray-500">
                    추방 가능한 멤버가 없습니다.
                  </p>
                ) : (
                  banableMembers.map((member) => (
                    <button
                      key={member.id}
                      onClick={() => handleSelectMember(member)}
                      className="w-full flex items-center justify-between p-3 rounded-lg border transition-all text-left border-gray-200 hover:border-red-300 hover:bg-red-50"
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {member.user_name}
                        </p>
                      </div>
                    </button>
                  ))
                )}
              </div>

              <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                <p className="font-medium mb-1">추방 권한 안내</p>
                <ul className="space-y-1 text-xs">
                  <li>• 추방 권한은 서버에서 확인됩니다</li>
                  <li>• 권한이 없는 경우 추방이 거부됩니다</li>
                  <li>• 본인은 추방할 수 없습니다</li>
                </ul>
              </div>
            </>
          ) : (
            <div>
              {/* 공유 해제될 콘텐츠 표시 */}
              {selectedMember &&
                (sharedFolders.length > 0 || sharedFiles.length > 0) && (
                  <div className="mb-4 p-4 bg-red-50 rounded-lg">
                    <h3 className="text-sm font-medium text-red-900 mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      추방 시 다음 콘텐츠가 더 이상 공유되지 않습니다
                    </h3>

                    {sharedFolders.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-red-800 mb-2">
                          하위 폴더 ({sharedFolders.length}개)
                        </p>
                        <div className="space-y-1 max-h-20 overflow-y-auto">
                          {sharedFolders.map((folder) => (
                            <div
                              key={folder.id}
                              className="flex items-center gap-2 text-xs text-red-700"
                            >
                              <Folder className="w-3 h-3" />
                              <span>{folder.folder_name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {sharedFiles.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-red-800 mb-2">
                          파일 ({sharedFiles.length}개)
                        </p>
                        <div className="space-y-1 max-h-20 overflow-y-auto">
                          {sharedFiles.map((file) => (
                            <div
                              key={file.id}
                              className="flex items-center gap-2 text-xs text-red-700"
                            >
                              <File className="w-3 h-3" />
                              <span>{file.file_name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-900 mb-1">
                      정말로 이 멤버를 추방하시겠습니까?
                    </p>
                    <p className="text-sm text-red-700">
                      <span className="font-semibold">
                        {selectedMember?.user_name}
                      </span>
                      님은 더 이상 이 폴더에 접근할 수 없게 됩니다.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    확인을 위해 멤버 이름을 입력하세요
                  </label>
                  <input
                    type="text"
                    placeholder={selectedMember?.user_name}
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          {showWarning ? (
            <>
              <button
                onClick={() => {
                  setShowWarning(false);
                  setSelectedMember(null);
                  setConfirmText("");
                  setError("");
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                뒤로
              </button>
              <button
                onClick={handleBan}
                disabled={confirmText !== selectedMember?.user_name || loading}
                className={`px-4 py-2 text-white rounded-md transition-colors ${
                  confirmText !== selectedMember?.user_name || loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {loading ? "추방 중..." : "추방하기"}
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              닫기
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BanFolderModal;
